import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import type { Tables } from "@/integrations/supabase/types";

const SELECTED_ORG_KEY = "supportfyi_selected_org";

interface OrgContextType {
  org: Tables<"organizations"> | null;
  orgs: Tables<"organizations">[];
  orgId: string | null;
  loading: boolean;
  switchOrg: (orgId: string) => void;
  refetch: () => Promise<void>;
}

const OrgContext = createContext<OrgContextType>({
  org: null,
  orgs: [],
  orgId: null,
  loading: true,
  switchOrg: () => {},
  refetch: async () => {},
});

export function OrgProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id ?? null;
  const [orgs, setOrgs] = useState<Tables<"organizations">[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchOrgs = useCallback(async () => {
    if (!userId) {
      setOrgs([]);
      setSelectedOrgId(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    const { data, error } = await supabase.rpc("get_my_orgs");

    if (error || !data) {
      setOrgs([]);
      setSelectedOrgId(null);
      setLoading(false);
      return;
    }

    const orgList = data as Tables<"organizations">[];
    setOrgs(orgList);

    // Restore previously selected org, or default to first
    const saved = localStorage.getItem(SELECTED_ORG_KEY);
    const match = orgList.find((o) => o.id === saved);
    setSelectedOrgId(match ? match.id : orgList[0]?.id ?? null);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    if (authLoading) return; // Wait for auth to settle first
    fetchOrgs();
  }, [fetchOrgs, authLoading]);

  const switchOrg = useCallback((orgId: string) => {
    setSelectedOrgId(orgId);
    localStorage.setItem(SELECTED_ORG_KEY, orgId);
  }, []);

  const org = orgs.find((o) => o.id === selectedOrgId) ?? null;

  return (
    <OrgContext.Provider
      value={{
        org,
        orgs,
        orgId: org?.id ?? null,
        loading,
        switchOrg,
        refetch: fetchOrgs,
      }}
    >
      {children}
    </OrgContext.Provider>
  );
}

export const useOrg = () => useContext(OrgContext);
